import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { useSharedValue } from "../components/Constants/constants";
import { useUserSettings } from "../contexts/UserSettingsContext";
import { BASE_URL, appVersion } from "../App_Settings/app_settings";
import "./Login.css";
import "../components/Constants/config.css";

const Login = () => {
  const history = useHistory();
  const { updateDirectNavigation, handleNotificationList, handleSelectedRows } = useSharedValue();
  const { refreshSettings } = useUserSettings();

  // Guards the SAML flow so it runs only once per page load, even if the
  // effect below re-runs because a dependency (e.g. a context function)
  // changes identity after mount.
  const samlFlowStarted = useRef(false);

  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);

  // Complete the SAML browser SSO handoff using the short-lived SAML cookie.
  useEffect(() => {
    if (samlFlowStarted.current) return;
    samlFlowStarted.current = true;

    const completeSamlLogin = async () => {
      console.log("[SAML] Login flow started", {
        url: window.location.href,
        baseUrl: BASE_URL,
      });

      const requestedProvider = new URLSearchParams(window.location.search).get("provider");
      const pathProvider = window.location.pathname === "/comply/login" ? "org2" : "org1";
      const configuredProvider = process.env.REACT_APP_SSO_PROVIDER;

      const provider = [requestedProvider, pathProvider, configuredProvider, "org1"]
        .map((value) => String(value || "").toLowerCase())
        .find((value) => value === "org1" || value === "org2");

      console.log("[SAML] Provider resolved:", {
        requestedProvider,
        pathProvider,
        configuredProvider,
        provider,
      });

      const storedToken = localStorage.getItem("token");
      const storedSessionToken = localStorage.getItem("sessionToken");

      if (storedToken && storedSessionToken) {
        console.log("[SAML] Existing token/session found. Validating session.");

        try {
          const tokenData = JSON.parse(storedToken);

          const sessionResponse = await fetch(`${BASE_URL}/api/SessionManagement/validate`, {
            credentials: "include",
            headers: {
              Authorization: `Bearer ${tokenData.token}`,
              "X-Session-Token": storedSessionToken,
            },
          });

          console.log("[SAML] Existing session validation:", {
            status: sessionResponse.status,
            ok: sessionResponse.ok,
          });

          if (sessionResponse.ok) {
            const sessionData = await sessionResponse.json();

            console.log("[SAML] Existing session validation response:", sessionData);

            if (sessionData.isValid === true) {
              console.log("[SAML] Existing session is valid. Redirecting to dashboard.");

              history.replace({ pathname: "/dashboard", state: { someValue: true } });
              return;
            }
          }
        } catch (error) {
          console.warn("[SAML] Stored session validation failed:", {
            error,
            message: error?.message,
            stack: error?.stack,
          });
        }

        console.log("[SAML] Removing invalid stored session.");

        localStorage.removeItem("token");
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("loginMethod");
      }

      // Use a timestamp instead of a flat flag so a previously-aborted SSO
      // attempt (network error, closed tab, browser back button, etc.) can't
      // permanently strand the user on this page. If more than SSO_TIMEOUT_MS
      // has passed since the redirect was started, treat it as stale and
      // retry the full round trip instead of calling /saml/session with no
      // cookie to show for it.
      const SSO_TIMEOUT_MS = 2 * 60 * 1000; // 2 minutes
      const inProgressAt = sessionStorage.getItem("samlLoginInProgress");
      const isStale = inProgressAt && Date.now() - Number(inProgressAt) > SSO_TIMEOUT_MS;

      console.log("[SAML] samlLoginInProgress:", inProgressAt, "isStale:", isStale);

      if (!inProgressAt || isStale) {
        sessionStorage.setItem("samlLoginInProgress", String(Date.now()));
        sessionStorage.setItem("samlProvider", provider);

        const loginUrl = `${BASE_URL}/saml/login?provider=${provider}`;

        console.log("[SAML] Starting SAML authentication:", loginUrl);

        window.location.assign(loginUrl);
        return;
      }

      console.log("[SAML] SAML redirect completed. Calling /saml/session.");

      try {
        console.log("[SAML] Requesting:", `${BASE_URL}/saml/session`);

        const res = await fetch(`${BASE_URL}/saml/session`, {
          credentials: "include",
        });

        console.log("[SAML] /saml/session response:", {
          status: res.status,
          statusText: res.statusText,
          ok: res.ok,
          contentType: res.headers.get("content-type"),
        });

        // Guard against an empty body (e.g. a 401 from the auth challenge
        // handler, which returns no content) so JSON.parse doesn't throw.
        const rawText = await res.text();
        const data = rawText ? JSON.parse(rawText) : {};

        console.log("[SAML] /saml/session response data:", data);

        if (!res.ok) {
          sessionStorage.removeItem("samlLoginInProgress");

          const errorMessage = data.message;

          // console.error("[SAML] Backend returned an error:", {
          //   status: res.status,
          //   message: errorMessage,
          //   data,
          //  });

          if (errorMessage) {
            window.alert(errorMessage);
          }
          return;
        }

        console.log("[SAML] Session handoff successful. Starting post-login processing.");

        localStorage.setItem("token", JSON.stringify(data));
        localStorage.setItem("loginMethod", "saml");

        if (data.sessionToken) {
          localStorage.setItem("sessionToken", data.sessionToken);
        }

        localStorage.removeItem("SelectedFilesList");
        localStorage.removeItem("notifications_list");
        localStorage.removeItem("preventAutoSSO");
        sessionStorage.removeItem("samlLoginInProgress");

        console.log("[SAML] Local/session storage updated successfully.");

        handleNotificationList([]);
        handleSelectedRows([]);

        console.log("[SAML] Notification/selection state reset.");

        updateDirectNavigation(true);

        console.log("[SAML] Direct navigation updated.");

        if (refreshSettings) {
          console.log("[SAML] Calling refreshSettings().");

          await refreshSettings();

          console.log("[SAML] refreshSettings() completed successfully.");
        }

        console.log("[SAML] Navigating to dashboard.");

        history.push({ pathname: "/dashboard", state: { someValue: true } });
      } catch (err) {
        sessionStorage.removeItem("samlLoginInProgress");

        console.error("[SAML] Session handoff failed.", {
          error: err,
          type: typeof err,
          name: err?.name,
          message: err?.message,
          stack: err?.stack,
        });

        window.alert("SSO login failed. Please try again or contact your administrator.");
      }
    };

    completeSamlLogin();
  }, [
    history,
    handleNotificationList,
    handleSelectedRows,
    refreshSettings,
    updateDirectNavigation,
  ]);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundImage: "var(--login-bg-image)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <CircularProgress style={{ color: "#fff" }} size={40} />
      <span
        style={{
          position: "fixed",
          bottom: 10,
          right: 14,
          color: "#ffffff",
          fontSize: "12px",
          pointerEvents: "none",
        }}
      >
        VERSION: {appVersion}
      </span>
    </div>
  );
};

export default Login;
